/**
 * NFC Solutions — "Save menu to home screen" for the digital menu. Session 30.29.
 *
 * Two jobs:
 *  1. While mounted, swap the page's <link rel="manifest"> to the per-tag
 *     manifest (/api/menu/manifest.webmanifest?tag=…) whose start_url is the
 *     SCAN url. Without this, "Add to Home screen" would install the global
 *     staff PWA (start_url "/") and the icon would open the staff login.
 *  2. A small bookmark button that opens a sheet with the native install
 *     prompt when the browser offers one (Android Chrome / desktop Chrome &
 *     Edge), otherwise step-by-step instructions per platform, plus a
 *     copyable link that always works as a browser bookmark or a manual
 *     desktop shortcut.
 */
import React, { useEffect, useState } from "react";
import { BookmarkPlus, Copy, Check, Smartphone, Monitor, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

let deferredInstallPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
  });
}

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

export const useMenuManifest = (tag) => {
  useEffect(() => {
    if (!tag || typeof document === "undefined") return undefined;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;
    const original = link.getAttribute("href");
    link.setAttribute("href", `${API}/menu/manifest.webmanifest?tag=${encodeURIComponent(tag)}`);
    return () => { if (original) link.setAttribute("href", original); };
  }, [tag]);
};

export const MenuShortcutButton = ({ tag, businessName, className = "" }) => {
  useMenuManifest(tag);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canPrompt, setCanPrompt] = useState(Boolean(deferredInstallPrompt));
  const platform = detectPlatform();
  const shortcutUrl = tag
    ? `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "")}/api/menu/scan?tag=${encodeURIComponent(tag)}`
    : "";

  useEffect(() => {
    const onPrompt = () => setCanPrompt(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferredInstallPrompt) return;
    try {
      deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        toast.success("Menu saved — find it on your home screen.");
        setOpen(false);
      }
    } catch (_) { /* user dismissed */ }
    deferredInstallPrompt = null;
    setCanPrompt(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shortcutUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Couldn't copy — long-press the link to copy it.");
    }
  };

  if (!tag) return null;
  const name = businessName || "this menu";

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className={`h-11 w-11 shrink-0 rounded-2xl bg-card ${className}`}
        onClick={() => setOpen(true)}
        title="Save this menu to your home screen"
        aria-label="Save this menu to your home screen"
        data-testid="menu-shortcut-button"
      >
        <BookmarkPlus className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-[28px]" data-testid="menu-shortcut-dialog">
          <DialogHeader className="text-left">
            <DialogTitle>Save {name} to your home screen</DialogTitle>
            <DialogDescription>
              Get a one-tap icon that opens this menu whenever you need it. If the menu is outside its opening hours, the icon shows when it will be back.
            </DialogDescription>
          </DialogHeader>

          {canPrompt ? (
            <Button className="h-11 w-full rounded-2xl" onClick={install} data-testid="menu-shortcut-install">
              <BookmarkPlus className="mr-2 h-4 w-4" /> Add to home screen
            </Button>
          ) : null}

          <div className="space-y-3 text-sm">
            {platform === "ios" ? (
              <div className="rounded-2xl border border-border bg-secondary/40 p-3" data-testid="menu-shortcut-steps-ios">
                <p className="mb-1 flex items-center gap-2 font-semibold"><Share className="h-4 w-4" /> iPhone / iPad (Safari)</p>
                <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                  <li>Tap the <span className="font-semibold text-foreground">Share</span> button at the bottom of Safari.</li>
                  <li>Scroll and tap <span className="font-semibold text-foreground">Add to Home Screen</span>, then <span className="font-semibold text-foreground">Add</span>.</li>
                </ol>
              </div>
            ) : null}
            {platform === "android" && !canPrompt ? (
              <div className="rounded-2xl border border-border bg-secondary/40 p-3" data-testid="menu-shortcut-steps-android">
                <p className="mb-1 flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4" /> Android (Chrome)</p>
                <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                  <li>Tap the <span className="font-semibold text-foreground">⋮</span> menu at the top right.</li>
                  <li>Tap <span className="font-semibold text-foreground">Add to Home screen</span> and confirm.</li>
                </ol>
              </div>
            ) : null}
            {platform === "desktop" ? (
              <div className="rounded-2xl border border-border bg-secondary/40 p-3" data-testid="menu-shortcut-steps-desktop">
                <p className="mb-1 flex items-center gap-2 font-semibold"><Monitor className="h-4 w-4" /> Windows / Mac (Chrome or Edge)</p>
                <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                  <li>Open the browser <span className="font-semibold text-foreground">⋮</span> menu.</li>
                  <li>Choose <span className="font-semibold text-foreground">Save and share → Create shortcut…</span> (Chrome) or <span className="font-semibold text-foreground">Apps → Install this site as an app</span> (Edge).</li>
                </ol>
              </div>
            ) : null}

            <div className="rounded-2xl border border-dashed border-border p-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">Or bookmark this link</p>
              <p className="break-all font-mono text-xs" data-testid="menu-shortcut-url">{shortcutUrl}</p>
              <Button variant="outline" size="sm" className="mt-2 h-9 rounded-xl" onClick={copy} data-testid="menu-shortcut-copy">
                {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />} {copied ? "Copied" : "Copy link"}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">Every open starts a fresh menu session, so this link never "expires" like a scanned page can.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
